import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generateFilterExpression } from '@/shared/utils/filterUtils';
import { useToast } from '@/shared/contexts/ToastContext';
import { FiltersetNameExistsError } from '@/shared/hooks/useFilterManagement';
import type { Filterset } from '@/app/services/filtersetService';
import { FiltersetEditorModal } from './FiltersetEditorModal';

interface SaveFiltersetModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (name: string, isShared: boolean) => Promise<void> | void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentFilters: Record<string, any>;
    /** Pre-formatted expression string. When provided, overrides the default generateFilterExpression(currentFilters). */
    filterExpression?: string;
    savedFilters?: Filterset[];
    targetModel?: string;
}

export const SaveFiltersetModal: React.FC<SaveFiltersetModalProps> = ({
    isOpen,
    onClose,
 onSave,
    currentFilters,
    filterExpression: filterExpressionProp,
    savedFilters,
    targetModel: targetModelProp,
}) => {
    const MAX_FILTER_NAME_LENGTH = 200;
    const { t } = useTranslation();
  const { showToast } = useToast();
    const [name, setName] = useState('');
    const [isShared, setIsShared] = useState(false);
    const [error, setError] = useState('');
    const [filterExpressionError, setFilterExpressionError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [exactMatch, setExactMatch] = useState<Filterset | null>(null);
    const [editableFilterExpression, setEditableFilterExpression] = useState(
  filterExpressionProp ?? generateFilterExpression(currentFilters)
    );
    const trimmedName = name.trim();
    const targetModel = targetModelProp ?? savedFilters?.[0]?.target_model;

    const handleClose = (): void => {
        if (isSaving) return;
        setName('');
        setIsShared(false);
   setError('');
   setFilterExpressionError('');
        setExactMatch(null);
        setEditableFilterExpression(filterExpressionProp ?? generateFilterExpression(currentFilters));
        onClose();
    };

    const handleSave = async (): Promise<void> => {
    let hasError = false;

      if (!trimmedName) {
   setError(t('selfServiceAccounts.filterset.nameRequired', 'Name is required'));
            hasError = true;
        } else if (trimmedName.length >= MAX_FILTER_NAME_LENGTH) {
            setError(
            t(
         'selfServiceAccounts.filterset.nameTooLong',
          'Name: Is too long (maximum is 200 characters)'
     )
         );
   hasError = true;
        } else if (exactMatch) {
   setError(t('shared.saveFilterset.nameAlreadyExists', 'Name: Has already been taken'));
 hasError = true;
        }

        if (!editableFilterExpression.trim()) {
      setFilterExpressionError(t('shared.saveFilterset.filterExpressionRequired', 'Filter expression is required'));
            hasError = true;
        }

        if (hasError) return;
        setIsSaving(true);
   try {
         await Promise.resolve(onSave(trimmedName, isShared));
   setName('');
      setIsShared(false);
            setError('');
            onClose();
            showToast(t('shared.saveFilterset.saveSuccess', 'Filter set saved successfully'), 'success');
     } catch (err) {
      if (err instanceof FiltersetNameExistsError) {
      showToast(
     t('shared.saveFilterset.nameAlreadyExists', 'Name: Has already been taken'),
          'error'
   );
            } else {
           showToast(t('shared.saveFilterset.saveError', 'Failed to save filter set'), 'error');
     }
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSaving) {
    handleClose();
     }
      };

      if (isOpen) {
         window.addEventListener('keydown', handleKeyDown);
        }

return () => {
    window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isSaving]);

    useEffect(() => {
        if (isOpen) {
      setEditableFilterExpression(filterExpressionProp ?? generateFilterExpression(currentFilters));
    setFilterExpressionError('');
        }
    }, [currentFilters, filterExpressionProp, isOpen]);

    if (!isOpen) return null;

    return (
 <FiltersetEditorModal
     isOpen={isOpen}
            onClose={handleClose}
     title={t('shared.saveFilterset.title', 'New Filter Set')}
            name={name}
  onNameChange={(value) => {
 setName(value);
             setError('');
          }}
 nameError={error}
     targetModel={targetModel}
      onExactNameMatchChange={setExactMatch}
            showShared
sharedInNameRow
       shared={isShared}
  onSharedChange={setIsShared}
            expression={editableFilterExpression}
            expressionError={filterExpressionError}
   editableExpression
            onExpressionChange={(value) => {
        setEditableFilterExpression(value);
         setFilterExpressionError('');
            }}
            saveLabel={t('common.create', 'Create')}
        savingLabel={t('common.creating', 'Creating...')}
  onSave={handleSave}
  isSaving={isSaving}
       dataTestId="save-filterset-modal"
        />
    );
};
